import { useDeleteConfirmation, useServices, useToast } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { useCrudNotifications, usePaginatedList, useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Amenity, AmenityReservation, ExpenseCategory, ReservationStatus, Unit, User as UserEntity } from '@/shared/types';
import type { QueryParams } from '@yawi/domain';
import { Ban, CalendarClock, Check, CheckCircle, Plus, Receipt, RefreshCw, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../atoms/Button';
import { AdminCreateReservationModal } from './AdminCreateReservationModal';

type ReservationFilters = {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const AdminReservationList = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { formatDate } = useDateFormatter();
  const { confirmDelete } = useDeleteConfirmation();
  const { amenityReservationRepo, amenityRepo, userRepo, expenseRepo, currentUser } = useServices();
  const ability = useAbility();
  const canUpdateReservation = ability.can('manage', 'all') || ability.can('update', 'Reservation');
  const canCreateReservation = ability.can('manage', 'all') || ability.can('create', 'Reservation');
  const { withNotification } = useCrudNotifications({
    entityName: 'Reservación',
  });

  // Deep link from dashboard "Revisar" button (?status=PENDING&search=<code>)
  const [searchParams] = useSearchParams();

  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Paginated list
  const {
    data: reservations,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList<AmenityReservation, ReservationFilters>({
    fetchFn: (params: QueryParams) => amenityReservationRepo.getReservations(params),
    initialFilters: { status: searchParams.get('status') || '', dateFrom: '', dateTo: '' },
    initialSearch: searchParams.get('search') || '',
    errorTitle: 'Error al cargar reservaciones',
  });

  // Load amenities and users for display
  useEffect(() => {
    Promise.all([
      amenityRepo.getAmenities({ limit: 100 }).then((r) => r.data),
      userRepo.getUsers({ limit: 100 }).then((r) => r.data)
    ]).then(([amData, uData]) => {
      setAmenities(amData);
      setUsers(uData);
    }).catch(console.error);
  }, [amenityRepo, userRepo]);

  const getAmenityName = (id: string) => {
    const am = amenities.find(a => a.id === id);
    return am ? am.name : id;
  };

  const normalizeValue = (value: unknown): string =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const getReservationUser = (reservation: AmenityReservation) => {
    const masterUser = reservation.member?.masterUserId
      ? users.find((user) => user.id === reservation.member?.masterUserId)
      : undefined;

    if (masterUser) return masterUser;

    const directUser = users.find((user) => user.id === reservation.memberId);
    if (directUser?.units?.length) return directUser;

    const memberKeys = [
      reservation.memberId,
      reservation.member?.id,
      reservation.memberName,
      reservation.member?.displayName,
      directUser?.email,
      directUser?.name,
    ]
      .map(normalizeValue)
      .filter(Boolean);

    const householdOwner = users.find((user) => (
      user.householdMembers?.some((member) => {
        const householdKeys = [
          member.id,
          member.email,
          member.name,
        ].map(normalizeValue);

        return householdKeys.some((key) => key && memberKeys.includes(key));
      })
    ));

    return householdOwner || directUser;
  };

  const getUnitName = (unit?: Unit | null): string => {
    if (unit?.name) return unit.name;
    if (unit?.tower) return `${unit.tower}${unit.number ? ` - ${unit.number}` : ''}`.trim();
    if (unit?.street) return `${unit.street}${unit.exteriorNumber ? ` #${unit.exteriorNumber}` : ''}`.trim();
    if (unit?.number) return unit.number;
    return '-';
  };

  const getUnitDisplayName = (user?: UserEntity): string => {
    const assignment = user?.units?.find((unitAssignment) => unitAssignment.isPrimary) || user?.units?.[0];
    const unitName = getUnitName(assignment?.unit);

    if (unitName !== '-') return unitName;
    if (user?.residenceInfo?.tower || user?.residenceInfo?.unit) {
      return `${user.residenceInfo.tower || ''} ${user.residenceInfo.unit || ''}`.trim();
    }
    return '-';
  };

  const getUserName = (id: string) => {
    const u = users.find(user => user.id === id);
    return u ? `${u.name} (${getUnitDisplayName(u)})` : id;
  };

  const handleRefundDeposit = async (reservation: AmenityReservation) => {
    if (!canUpdateReservation) return;

    await confirmDelete({
      title: 'Devolver Deposito',
      itemName: `$${reservation.depositAmount} - ${reservation.member?.displayName}`,
      itemType: 'Deposito',
      warningMessage: 'Se creara un registro de egreso automaticamente.',
      onConfirm: async () => {
        setProcessingId(reservation.id);
        try {
          await withNotification(async () => {
            // 1. Create Expense Record
            await expenseRepo.createExpense({
              title: `Devolución Depósito Reserva #${reservation.reservationCode}`,
              amount: reservation.depositAmount,
              category: ExpenseCategory.REFUND,
              date: new Date().toISOString().split('T')[0],
              status: 'PAID',
              provider: reservation.member?.displayName
            });

            // 2. Update Reservation Status
            await amenityReservationRepo.refundDeposit(reservation.id, { fullRefund: true });
          }, 'Depósito devuelto y egreso registrado correctamente.');
          refresh();
        } catch {
          // Error ya mostrado por el hook
        } finally {
          setProcessingId(null);
        }
      }
    });
  };

  const handleConfirmReservation = async (reservation: AmenityReservation) => {
    if (!canUpdateReservation) return;

    setConfirmingId(reservation.id);
    try {
      await withNotification(async () => {
        await amenityReservationRepo.confirmReservation(reservation.id);
      }, 'Reservación confirmada y cargos generados correctamente.');
      refresh();
    } catch {
      // Error ya mostrado por el hook
    } finally {
      setConfirmingId(null);
    }
  };

  const handleCancelReservation = async (reservation: AmenityReservation) => {
    if (!canUpdateReservation) return;

    await confirmDelete({
      title: 'Cancelar Reservación',
      itemName: `${reservation.reservationCode || reservation.id} - ${reservation.member?.displayName}`,
      itemType: 'Reservación',
      warningMessage: 'Esta acción no se puede deshacer.',
      onConfirm: async () => {
        setCancellingId(reservation.id);
        try {
          await withNotification(async () => {
            await amenityReservationRepo.cancelReservation(reservation.id);
          }, 'Reservación cancelada correctamente.');
          refresh();
        } catch {
          // Error ya mostrado por el hook
        } finally {
          setCancellingId(null);
        }
      }
    });
  };

  const getStatusBadge = (status: ReservationStatus) => {
    switch (status) {
      case ReservationStatus.CONFIRMED:
        return (
          <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold border border-green-200 flex items-center gap-1 w-fit">
            <CheckCircle size={8}/> Confirmada
          </span>
        );
      case ReservationStatus.PENDING:
        return (
          <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200 w-fit">
            Pendiente
          </span>
        );
      case ReservationStatus.CANCELLED:
        return (
          <span className="text-[10px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-200 w-fit">
            Cancelada
          </span>
        );
      default:
        return null;
    }
  };

  // Column definitions
  const columns: ColumnDef<AmenityReservation>[] = useMemo(() => [
    {
      id: 'code',
      header: t('admin.reservations.id'),
      cell: (res) => (
        <span className="text-xs font-mono text-slate-400">
          {res.reservationCode || res.id}
        </span>
      ),
    },
    {
      id: 'user',
      header: t('admin.reservations.user'),
      cell: (res) => (
        <div className="flex items-center gap-2 font-medium text-slate-800 whitespace-nowrap">
          <User size={14} className="text-slate-400 flex-shrink-0"/>
          {res.member?.displayName || getUserName(res.memberId)}
        </div>
      ),
    },
    {
      id: 'unit',
      header: 'Unidad',
      cell: (res) => (
        <span className="text-slate-600">
          {getUnitName(res.member?.unit) !== '-'
            ? getUnitName(res.member?.unit)
            : getUnitDisplayName(getReservationUser(res))}
        </span>
      ),
    },
    {
      id: 'amenity',
      header: t('amenities.list.amenity'),
      cell: (res) => (
        <span className="text-slate-600">{res.amenity?.name ?? getAmenityName(res.amenityId)}</span>
      ),
    },
    {
      id: 'date',
      header: t('amenities.list.date'),
      cell: (res) => (
        <span className="text-slate-600">
          {formatDate(res.date)} <span className="text-xs text-slate-400 ml-1">{res.startTime}</span>
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (res) => getStatusBadge(res.status),
    },
    {
      id: 'cost',
      header: t('amenities.list.cost'),
      cell: (res) => (
        <span className="font-mono text-slate-700">${res.totalCost}</span>
      ),
    },
    {
      id: 'deposit',
      header: 'Depósito',
      cell: (res) => (
        res.depositAmount > 0 ? (
          <div className="flex flex-col items-start gap-1">
            <span className="font-mono text-amber-600 font-bold">${res.depositAmount}</span>
            {res.isRefunded ? (
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold border border-green-200 flex items-center gap-1">
                <CheckCircle size={8}/> Devuelto
              </span>
            ) : (
              <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                En Garantía
              </span>
            )}
          </div>
        ) : (
          <span className="text-slate-400">-</span>
        )
      ),
    },
    {
      id: 'charges',
      header: 'Cargos',
      cell: (res) => (
        (res.serviceChargeId || res.depositChargeId) ? (
          <div className="flex flex-col gap-1">
            {res.serviceChargeId && (
              <a
                href={`/pagos?chargeId=${res.serviceChargeId}`}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Receipt size={12}/> Servicio
              </a>
            )}
            {res.depositChargeId && (
              <a
                href={`/pagos?chargeId=${res.depositChargeId}`}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Receipt size={12}/> Depósito
              </a>
            )}
          </div>
        ) : (
          <span className="text-slate-400 text-xs">Sin cargos</span>
        )
      ),
    },
  ], [t, amenities, users, formatDate]);

  return (
    <>
    <DataTable
      data={reservations}
      columns={columns}
      keyExtractor={(res) => res.id}
      meta={meta}
      onPageChange={setPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      title={t('admin.reservations.title')}
      actions={canCreateReservation ? (
        <Button onClick={() => setIsCreateModalOpen(true)} icon={<Plus size={16}/>}>
          Nueva reservación
        </Button>
      ) : undefined}
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar por codigo, usuario o amenidad..."
      filters={
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:min-w-[520px]">
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => setFilters({ dateFrom: e.target.value })}
            aria-label="Fecha desde"
            className="h-[38px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => setFilters({ dateTo: e.target.value })}
            aria-label="Fecha hasta"
            className="h-[38px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <select
            value={filters.status || ''}
            onChange={(event) => setFilters({ status: event.target.value })}
            className="h-[38px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todos los estados</option>
            <option value={ReservationStatus.PENDING}>Pendientes</option>
            <option value={ReservationStatus.CONFIRMED}>Confirmadas</option>
            <option value={ReservationStatus.CANCELLED}>Canceladas</option>
          </select>
        </div>
      }
      loading={loading}
      emptyMessage="No se encontraron reservaciones."
      emptyIcon={(
        <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
          <CalendarClock className="w-8 h-8 text-primary-400" />
        </div>
      )}
      rowActions={(res) => (
        <div className="flex gap-1">
          {canUpdateReservation && res.status !== ReservationStatus.CANCELLED && (
            <Button
              size="sm"
              variant="danger"
              icon={<Ban size={14}/>}
              onClick={() => handleCancelReservation(res)}
              isLoading={cancellingId === res.id}
              className="text-xs"
            >
              Cancelar
            </Button>
          )}
          {canUpdateReservation && res.status === ReservationStatus.PENDING && (
            <Button
              size="sm"
              variant="primary"
              icon={<Check size={14}/>}
              onClick={() => handleConfirmReservation(res)}
              isLoading={confirmingId === res.id}
              className="text-xs"
            >
              Confirmar
            </Button>
          )}
          {canUpdateReservation && res.depositAmount > 0 && !res.isRefunded && res.status === ReservationStatus.CONFIRMED && (
            <Button
              size="sm"
              variant="secondary"
              icon={<RefreshCw size={14}/>}
              onClick={() => handleRefundDeposit(res)}
              isLoading={processingId === res.id}
              className="text-xs"
            >
              Devolver
            </Button>
          )}
        </div>
      )}
    />

    {isCreateModalOpen && (
      <AdminCreateReservationModal
        amenities={amenities}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={refresh}
      />
    )}
    </>
  );
};
